import { MailerSendConfig, UpgradeEmailData } from '@/types/subscriptionUpgrade';
import { supabase } from '@/integrations/supabase/client';

/**
 * Interface for email service implementations
 */
interface IEmailService {
  sendUpgradeNotification(data: UpgradeEmailData): Promise<boolean>;
  sendTestEmail(toEmail: string): Promise<boolean>;
  sendBulkUpgradeNotifications(emailDataArray: UpgradeEmailData[]): Promise<{
    successful: number;
    failed: number;
    errors: Array<{ email: string; error: string }>;
  }>;
  getStatus(): { initialized: boolean; fromEmail: string; fromName: string };
}

/**
 * Email service that uses Supabase Edge Functions to send emails
 * This avoids the client-side MailerSend issues by delegating to the backend
 */
export class EmailService implements IEmailService {
  private config: MailerSendConfig;
  private isInitialized: boolean = false;

  constructor(config: MailerSendConfig) {
    this.config = config;
    this.initialize();
  }

  /**
   * Initialize the service - just validate config
   */
  private initialize(): void {
    try {
      if (!this.config.fromEmail) {
        console.warn('[EmailService] No from email configured, using default');
        this.config.fromEmail = 'noreply@keepmore.com';
      }

      this.isInitialized = true;
      console.log('[EmailService] Successfully initialized Email Service');
    } catch (error) {
      console.error('[EmailService] Failed to initialize Email Service:', error);
      throw error;
    }
  }

  /**
   * Send subscription upgrade notification email via Supabase Edge Function
   */
  async sendUpgradeNotification(data: UpgradeEmailData): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        throw new Error('EmailService not initialized');
      }

      // Validate email data
      this.validateEmailData(data);

      console.log(`[EmailService] Sending upgrade notification to ${data.customerEmail}`);

      // Get the current session - same pattern as externalSubscriptionService
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User session not found. Please log in.');
      }

      const { data: result, error } = await supabase.functions.invoke('send-upgrade-email', {
        body: {
          emailData: data,
          fromEmail: this.config.fromEmail,
          fromName: data.fromName || this.config.fromName || 'KeepMore',
          replyTo: data.replyTo || this.config.fromEmail
        }
      });

      if (error) {
        console.error('[EmailService] Edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!result) {
        console.warn('[EmailService] No data received from edge function');
        throw new Error('No response from email service');
      }

      if (!result?.success) {
        console.error('[EmailService] Edge function returned error:', result);
        throw new Error(result?.error || 'Unknown error from email service');
      }

      console.log(`[EmailService] Email sent successfully to ${data.customerEmail}`, {
        messageId: result.messageId,
        timestamp: result.timestamp
      });
      
      return true;

    } catch (error: any) {
      console.error('[EmailService] Failed to send upgrade notification:', {
        error: error.message,
        customerEmail: data.customerEmail
      });

      // Re-throw with more context
      throw new Error(`Failed to send email to ${data.customerEmail}: ${error.message}`);
    }
  }

  /**
   * Send test email to verify configuration
   */
  async sendTestEmail(toEmail: string, emailData?: UpgradeEmailData): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        throw new Error('EmailService not initialized');
      }

      console.log(`[EmailService] Sending test email to ${toEmail}`);

      // Get the current session - same pattern as externalSubscriptionService
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User session not found. Please log in.');
      }

      const body: { test: boolean; toEmail: string; fromEmail: string; fromName: string; emailData?: UpgradeEmailData } = {
        test: true,
        toEmail,
        fromEmail: this.config.fromEmail,
        fromName: this.config.fromName || 'KeepMore'
      };
      if (emailData) body.emailData = emailData;

      const { data: result, error } = await supabase.functions.invoke('send-upgrade-email', { body });

      if (error) {
        console.error('[EmailService] Edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!result) {
        console.warn('[EmailService] No data received from edge function');
        throw new Error('No response from email service');
      }

      if (!result?.success) {
        console.error('[EmailService] Edge function returned error:', result);
        throw new Error(result?.error || 'Unknown error from email service');
      }

      console.log(`[EmailService] Test email sent successfully to ${toEmail}`, result);
      return true;

    } catch (error: any) {
      console.error('[EmailService] Failed to send test email:', error);
      throw error;
    }
  }

  /**
   * Send bulk upgrade notification emails
   */
  async sendBulkUpgradeNotifications(emailDataArray: UpgradeEmailData[]): Promise<{
    successful: number;
    failed: number;
    errors: Array<{ email: string; error: string }>;
  }> {
    const results = {
      successful: 0,
      failed: 0,
      errors: [] as Array<{ email: string; error: string }>
    };

    console.log(`[EmailService] Sending bulk upgrade notifications to ${emailDataArray.length} recipients`);

    // Send emails in batches to avoid overwhelming the service
    const batchSize = 5;
    for (let i = 0; i < emailDataArray.length; i += batchSize) {
      const batch = emailDataArray.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async emailData => {
        try {
          await this.sendUpgradeNotification(emailData);
          results.successful++;
        } catch (error: any) {
          results.failed++;
          results.errors.push({
            email: emailData.customerEmail,
            error: error.message
          });
        }
      });

      // Wait for current batch to complete before starting next batch
      await Promise.allSettled(batchPromises);
      
      // Small delay between batches to be respectful to the email service
      if (i + batchSize < emailDataArray.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    console.log(`[EmailService] Bulk email results:`, results);
    return results;
  }

  /**
   * Validate email data before sending
   */
  private validateEmailData(data: UpgradeEmailData): void {
    const requiredFields = [
      { field: 'customerEmail', value: data.customerEmail },
      { field: 'subscriptionName', value: data.subscriptionName },
      { field: 'currentAmount', value: data.currentAmount },
      { field: 'newAmount', value: data.newAmount },
      { field: 'gracePeriodEnd', value: data.gracePeriodEnd },
      { field: 'changePaymentMethodUrl', value: data.changePaymentMethodUrl },
      { field: 'supportEmail', value: data.supportEmail }
    ];

    const missingFields = requiredFields.filter(({ value }) => 
      value === undefined || value === null || value === ''
    );

    if (missingFields.length > 0) {
      throw new Error(`Missing required email data fields: ${missingFields.map(f => f.field).join(', ')}`);
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.customerEmail)) {
      throw new Error(`Invalid customer email format: ${data.customerEmail}`);
    }

    if (!emailRegex.test(data.supportEmail)) {
      throw new Error(`Invalid support email format: ${data.supportEmail}`);
    }

    // Validate amounts
    if (data.currentAmount < 0 || data.newAmount < 0) {
      throw new Error('Amounts must be positive numbers');
    }

    // Validate grace period date
    const gracePeriodDate = new Date(data.gracePeriodEnd);
    if (isNaN(gracePeriodDate.getTime())) {
      throw new Error('Invalid grace period date format');
    }

    // Validate URL format
    try {
      new URL(data.changePaymentMethodUrl);
    } catch {
      throw new Error('Invalid change payment method URL format');
    }
  }

  /**
   * Format currency for display
   */
  private formatCurrency(amountInCents: number): string {
    return (amountInCents / 100).toFixed(2);
  }

  /**
   * Get service status and configuration
   */
  getStatus(): { initialized: boolean; fromEmail: string; fromName: string } {
    return {
      initialized: this.isInitialized,
      fromEmail: this.config.fromEmail,
      fromName: this.config.fromName || 'KeepMore'
    };
  }
}

/**
 * Factory function to create EmailService instance with configuration
 * This version uses Supabase Edge Functions instead of direct MailerSend
 */
export function createEmailService(config?: MailerSendConfig): EmailService {
  // Try to get config from Vite environment variables
  const envConfig: MailerSendConfig = {
    apiKey: '', // Not needed for Supabase Edge Function approach
    fromEmail: import.meta.env.VITE_FROM_EMAIL || 'noreply@keepmore.co',
    fromName: import.meta.env.VITE_FROM_NAME || 'KeepMore'
  };

  // Use provided config or env config
  const finalConfig = config || envConfig;

  console.log('[EmailService] Creating email service via Supabase Edge Functions');
  return new EmailService(finalConfig);
}


